import { FC } from 'react';
import { cn } from '@crisellevaldez/speculo-ui';
import { ArrowDownLeft, ArrowUpRight, ArrowRight } from 'lucide-react';
import { StatsModal } from '../StatsModal';
import { formatDate } from '../../../../utils/dateTime';
import { formatPhoneNumber } from '../../../../utils/formatters';
import { avatarTint, avatarInitial } from '../../../../utils/avatar';
import { linkify } from '../../../../utils/linkify';
import type { useConversationThreadsGQL } from '../../../../hooks/graphql/dashboard/useConversationThreadsGQL';
import { CrmStageDisplay } from '../../../common/CrmStageDisplay';
import { ThreadCount } from '../../../common/ThreadCount';
import { useShowCrmStage } from '../../../../hooks/useShowCrmStage';
import type { CrmStageProjection } from '../../../../types/crmStage.types';

interface SmsThreadSelection {
  contactId: string;
  contactName: string | null;
  contactPhone: string | null;
  /** Row's `messageCount` — total SMS for the contact (or within the
   *  active date filter, per backend SPEC-1907). Threaded to the modal
   *  subtitle so it reads "107 messages" the same way SmsTab does. */
  totalCount: number | null;
  /**
   * Anchor message — what the row's `matchedMessageId` (newest match in
   * the active date range, or thread's most-recent) plus the row's
   * latest-message preview/direction/timestamp. SmsThreadModal opens
   * scrolled to this bubble.
   */
  anchor: {
    messageId: string;
    body: string | null;
    direction: string | null;
    sentAt: string | null;
  } | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dateRange: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  timezone?: string;
  smsThreadsGQL: ReturnType<typeof useConversationThreadsGQL>;
  /** Whole-row click — opens the contact's edit modal. */
  onOpenContact: (contactId: string) => void;
  /** "Open thread →" button — opens the dedicated SmsThreadModal. */
  onOpenThread: (selection: SmsThreadSelection) => void;
}

/**
 * SMS drill-down — inbox-style rows (per-contact avatar tint,
 * directional arrow, phone tel: link, hover "Open thread" affordance).
 * Backed by `conversationThreads` so back-and-forth messages with the
 * same lead collapse into a single row.
 */
export const SmsStatsModal: FC<Props> = ({
  isOpen,
  onClose,
  dateRange,
  onDateRangeChange,
  timezone,
  smsThreadsGQL,
  onOpenContact,
  onOpenThread,
}) => {
  const showCrmStage = useShowCrmStage();
  if (!isOpen) return null;
  return (
    <StatsModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        smsThreadsGQL.setSearch?.(undefined);
      }}
      title="SMS"
      dateRange={dateRange}
      onDateRangeChange={onDateRangeChange}
      onSearch={(q) => smsThreadsGQL.setSearch?.(q || undefined)}
      searchPlaceholder="Search by name or message..."
      currentPage={smsThreadsGQL.currentPage}
      pageSize={smsThreadsGQL.pageSize}
      onNextPage={smsThreadsGQL.nextPage}
      onPrevPage={smsThreadsGQL.prevPage}
      onPageChange={smsThreadsGQL.goToPage}
      onPageSizeChange={smsThreadsGQL.changePageSize}
      totalCount={smsThreadsGQL.total}
      isLoading={smsThreadsGQL.isLoading}
      // Whole-row click opens the contact's edit modal so the user can
      // browse the full SMS tab. The "Open thread →" button inside the
      // row gives a focused alternative that opens just the thread.
      onRowClick={(item) => {
        const id = item.contactId as string | undefined;
        if (id) onOpenContact(id);
      }}
      columns={[
        {
          key: 'thread',
          header: 'Conversations',
          cell: (item: Record<string, unknown>) => {
            const name = item.contactName as string | undefined;
            const contactId = item.contactId as string | undefined;
            const phone = item.contactPhone as string | undefined;
            const matchedMessageId = (item.matchedMessageId as string | undefined) ?? null;
            const preview = (item.lastMessagePreview as string | undefined) || '';
            const dir = (item.lastDirection as string | undefined)?.toLowerCase();
            const isInbound = dir === 'inbound';
            const count = (item.messageCount as number | undefined) ?? 0;
            const ts = item.lastMessageAt as string | undefined;
            const crmStages = item.crmStages as CrmStageProjection[] | undefined;
            const displayName = name && name !== 'unknown' ? name : null;

            // Inbox-style row: avatar bubble · two-line content · meta on the
            // right. Mirrors Gmail/iMessage hierarchy — name + time on top,
            // preview as the dominant body line, meta in a smaller muted footer.
            const initial = avatarInitial(displayName, phone);
            const tint = avatarTint(contactId || displayName || phone);
            return (
              <div className="group flex items-start gap-2.5 sm:gap-3 py-1.5 min-w-0 cursor-pointer">
                {/* Per-contact avatar tint — stable colour hashed off the
                    contact id (or name/phone fallback) so the same person
                    always renders the same bubble across reloads.
                    Direction is communicated by the arrow icon below. */}
                <div
                  className={cn(
                    'flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ring-2',
                    tint
                  )}
                >
                  {initial}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Top row — name (link) + timestamp on the right.
                      No explicit font-size — inherits STAT_CELL_TEXT
                      from the parent <td> so the SMS row matches every
                      other stats cell at every breakpoint. */}
                  <div className="flex items-baseline justify-between gap-2">
                    {contactId && displayName ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenContact(contactId);
                        }}
                        className="text-primaryfortext font-semibold underline text-left truncate"
                      >
                        {displayName}
                      </button>
                    ) : (
                      <span className="font-semibold text-foreground truncate">
                        {displayName ?? (phone ? formatPhoneNumber(phone) : '—')}
                      </span>
                    )}
                    {ts && (
                      <span className="text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {formatDate(ts, timezone)}
                      </span>
                    )}
                  </div>

                  {/* Preview — directional arrow + last message. Uses
                      the same blue/green pair as the call channel
                      (inbound = blue, outbound from Remi = green). */}
                  {preview && (
                    <div className="flex items-start gap-1.5 sm:gap-2 mt-1 text-foreground/90">
                      {isInbound ? (
                        <ArrowDownLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 call-color-inbound flex-shrink-0 mt-0.5" />
                      ) : (
                        <ArrowUpRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 call-color-outbound flex-shrink-0 mt-0.5" />
                      )}
                      <span className="block min-w-0 max-w-[1000px] truncate" title={preview}>
                        {linkify(preview)}
                      </span>
                    </div>
                  )}

                  {/* Meta footer — phone · count · "Open thread" action.
                      Muted colour keeps it visually subordinate; the
                      Open-thread button is the one place inside the row
                      that opens the thread viewer (whole-row click
                      still opens the contact edit modal). */}
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 text-muted-foreground flex-wrap min-w-0">
                    {showCrmStage && (
                      <>
                        <CrmStageDisplay stages={crmStages} compact />
                        <span>·</span>
                      </>
                    )}
                    {phone && (
                      <a
                        href={`tel:${phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="tabular-nums whitespace-nowrap hover:text-primaryfortext hover:underline"
                      >
                        {formatPhoneNumber(phone)}
                      </a>
                    )}
                    {phone && <span>·</span>}
                    <ThreadCount count={count} channel="sms" />
                    {contactId && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenThread({
                            contactId,
                            contactName: displayName,
                            contactPhone: phone ?? null,
                            totalCount: count,
                            anchor: matchedMessageId
                              ? {
                                  messageId: matchedMessageId,
                                  body: preview || null,
                                  direction: dir ?? null,
                                  sentAt: ts ?? null,
                                }
                              : null,
                          });
                        }}
                        className="inline-flex items-center gap-1 text-primaryfortext font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity hover:underline"
                      >
                        Open thread
                        <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          },
        },
      ]}
      data={smsThreadsGQL.items || []}
    />
  );
};

export default SmsStatsModal;
